import { useCallback, useEffect, useState } from 'react';
import { MediaTypeOptions } from 'expo-image-picker';

import { useImagePicker } from './useImagePicker';
import { usePostsModel } from '@models/Posts/PostsModel';
import { ToastService } from '@services';
import { UploadMedia } from '@models/Posts/UploadMediaModel';
import { MediaTypeEnum } from '@constants';
import { Post } from '@models/Posts/PostModel';

export function usePostEditor(item?: Post) {
  const imagePicker = useImagePicker();
  const [text, setText] = useState(item?.itemData.text ?? '');
  const posts = usePostsModel();

  useEffect(() => {
    if (item) {
      // Set media for update
      posts.uploadMediaList.set(
        item.itemData.images.map((image) => ({
          id: image.id,
          uri: image.normal?.url as string,
          type: MediaTypeEnum.Image,
        })),
      );
    }

    return () => {
      posts.uploadMediaList.clear();
    };
  }, [item]);

  async function submitPost() {
    const dataToSubmit = {
      text,
      ...posts.uploadMediaList.dataToUpload,
    };

    if (isUpdate) {
      await item?.update.run(dataToSubmit);
    } else {
      await posts.create.run(dataToSubmit);
    }
  }

  function mediaType() {
    // TODO: uncomment this code if you need to enable video support
    // if (posts.uploadMediaList.hasVideo) {
    //   return MediaTypeOptions.Videos;
    // } else if (posts.uploadMediaList.hasImage) {
    //   return MediaTypeOptions.Images;
    // } else {
    //   return MediaTypeOptions.All;
    // }

    return MediaTypeOptions.Images;
  }

  async function openPickerToAddMedia() {
    try {
      const res = await imagePicker.open({
        pickerOpts: { mediaTypes: mediaType() },
      });

      if (res) {
        posts.uploadMediaList.add({
          uri: res.uri,
          type: res.type as MediaTypeEnum,
        });
      }
    } catch (error) {
      ToastService.showError((error as Error).message);
    }
  }

  const removeSelectedMedia = useCallback((media: UploadMedia) => {
    posts.uploadMediaList.remove(media.id);
  }, []);

  let isValidPost = false;

  if (posts.uploadMediaList.isEmpty) {
    isValidPost = !!text.trim();
  } else {
    isValidPost = posts.uploadMediaList.isAllMediaUploaded;
  }

  if (item && isValidPost) {
    // Check current data and the data of the item are different, if it is not then it
    // does not make sense to send a request for updating the post, because nothing changed
    const isTextHasBeenChanged = item.itemData.text !== text;

    const itemImgIds = item.itemData.images.map((el) => el.id);
    const isImagesHasBeenChanged = posts.uploadMediaList.ids.some(
      (id) => !itemImgIds.includes(id),
    );

    isValidPost = isTextHasBeenChanged || isImagesHasBeenChanged;
  }

  const isUpdate = !!item;

  const inProgress = isUpdate
    ? item?.update.inProgress
    : posts.create.inProgress;

  return {
    text,
    setText,
    isValidPost,
    openPickerToAddMedia,
    removeSelectedMedia,
    isUpdate,
    submitPost,
    inProgress,
  };
}
